import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import DictationInput from '../components/DictationInput';
import { Users, GraduationCap, Heart, Clock, User, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import AlumniBrowseTab from '../components/alumni/AlumniBrowseTab';
import AlumniConnectionsTab from '../components/alumni/AlumniConnectionsTab';
import AlumniRequestsTab from '../components/alumni/AlumniRequestsTab';

export default function AlumniNetworkPage() {
  const { user, axiosAuth } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [myProfile, setMyProfile] = useState(null);
  const [myRequests, setMyRequests] = useState({ as_mentee: [], as_mentor: [] });
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [mentorsOnly, setMentorsOnly] = useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [connectMessage, setConnectMessage] = useState('');
  const [areasOfInterest, setAreasOfInterest] = useState('');
  const api = axiosAuth();

  const [profileForm, setProfileForm] = useState({
    bio: '', success_story: '', skills: '', available_for_mentorship: false,
    mentorship_areas: '', profile_visible: true, program_completed_date: '',
    current_employment: '', advice_for_participants: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profilesRes, myProfileRes, requestsRes, connectionsRes] = await Promise.all([
        api.get(`/alumni/profiles${mentorsOnly ? '?available_for_mentorship=true' : ''}`),
        api.get('/alumni/my-profile'),
        api.get('/mentorship/my-requests'),
        api.get('/mentorship/connections')
      ]);
      setProfiles(profilesRes.data.profiles || []);
      setMyProfile(myProfileRes.data);
      setMyRequests(requestsRes.data);
      setConnections(connectionsRes.data.connections || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [mentorsOnly]);

  useEffect(() => {
    if (myProfile && myProfile.is_alumni) {
      setProfileForm({
        bio: myProfile.bio || '', success_story: myProfile.success_story || '',
        skills: myProfile.skills?.join(', ') || '', available_for_mentorship: myProfile.available_for_mentorship || false,
        mentorship_areas: myProfile.mentorship_areas?.join(', ') || '', profile_visible: myProfile.profile_visible !== false,
        program_completed_date: myProfile.program_completed_date || '', current_employment: myProfile.current_employment || '',
        advice_for_participants: myProfile.advice_for_participants || ''
      });
    }
  }, [myProfile]);

  const handleSaveProfile = async () => {
    try {
      const params = new URLSearchParams();
      if (profileForm.bio) params.append('bio', profileForm.bio);
      if (profileForm.success_story) params.append('success_story', profileForm.success_story);
      if (profileForm.skills) params.append('skills', profileForm.skills);
      params.append('available_for_mentorship', profileForm.available_for_mentorship);
      if (profileForm.mentorship_areas) params.append('mentorship_areas', profileForm.mentorship_areas);
      params.append('profile_visible', profileForm.profile_visible);
      if (profileForm.program_completed_date) params.append('program_completed_date', profileForm.program_completed_date);
      if (profileForm.current_employment) params.append('current_employment', profileForm.current_employment);
      if (profileForm.advice_for_participants) params.append('advice_for_participants', profileForm.advice_for_participants);
      await api.put(`/alumni/my-profile?${params.toString()}`);
      toast.success('Profile updated!');
      setProfileDialogOpen(false);
      fetchData();
    } catch {
      toast.error('Failed to update profile');
    }
  };

  const requestMentorship = async () => {
    if (!selectedMentor) return;
    try {
      await api.post(`/mentorship/request?mentor_user_id=${selectedMentor.user_id}&message=${encodeURIComponent(connectMessage)}&areas_of_interest=${encodeURIComponent(areasOfInterest)}`);
      toast.success('Mentorship request sent!');
      setConnectDialogOpen(false);
      setConnectMessage('');
      setAreasOfInterest('');
      setSelectedMentor(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    }
  };

  const respondToRequest = async (requestId, action) => {
    try {
      await api.put(`/mentorship/request/${requestId}/respond?action=${action}`);
      toast.success(`Request ${action}`);
      fetchData();
    } catch {
      toast.error('Failed to respond');
    }
  };

  const openConnectDialog = (mentor) => {
    setSelectedMentor(mentor);
    setConnectDialogOpen(true);
  };

  if (loading && !profiles.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="alumni-network-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <GraduationCap className="text-emerald-600" /> Alumni Network
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">Connect with program graduates and find mentors</p>
        </div>
        <Button onClick={() => setProfileDialogOpen(true)} variant="outline" className="rounded-full gap-2">
          <User size={16} /> My Alumni Profile
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4"><div className="flex items-center gap-2 mb-2"><div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><GraduationCap className="text-emerald-600" size={20} /></div></div><p className="text-2xl font-bold">{profiles.length}</p><p className="text-xs text-muted-foreground">Alumni</p></CardContent></Card>
        <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4"><div className="flex items-center gap-2 mb-2"><div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center"><Heart className="text-violet-600" size={20} /></div></div><p className="text-2xl font-bold">{profiles.filter(p => p.available_for_mentorship).length}</p><p className="text-xs text-muted-foreground">Available Mentors</p></CardContent></Card>
        <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4"><div className="flex items-center gap-2 mb-2"><div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center"><Users className="text-sky-600" size={20} /></div></div><p className="text-2xl font-bold">{connections.length}</p><p className="text-xs text-muted-foreground">My Connections</p></CardContent></Card>
        <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4"><div className="flex items-center gap-2 mb-2"><div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><Clock className="text-amber-600" size={20} /></div></div><p className="text-2xl font-bold">{myRequests.as_mentee.filter(r => r.status === 'pending').length}</p><p className="text-xs text-muted-foreground">Pending Requests</p></CardContent></Card>
      </div>

      <Tabs defaultValue="browse" className="space-y-4">
        <TabsList className="bg-muted/50 rounded-full p-1">
          <TabsTrigger value="browse" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">Browse Alumni</TabsTrigger>
          <TabsTrigger value="connections" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">My Connections ({connections.length})</TabsTrigger>
          <TabsTrigger value="requests" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">Requests ({myRequests.as_mentee.length + myRequests.as_mentor.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="browse"><AlumniBrowseTab profiles={profiles} searchQuery={searchQuery} setSearchQuery={setSearchQuery} mentorsOnly={mentorsOnly} setMentorsOnly={setMentorsOnly} openConnectDialog={openConnectDialog} /></TabsContent>
        <TabsContent value="connections"><AlumniConnectionsTab connections={connections} userId={user.id} /></TabsContent>
        <TabsContent value="requests"><AlumniRequestsTab myRequests={myRequests} user={user} respondToRequest={respondToRequest} /></TabsContent>
      </Tabs>

      {/* Request Mentorship Dialog */}
      <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Request Mentorship</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            {selectedMentor && (
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center"><GraduationCap className="text-emerald-600" size={24} /></div>
                <div><p className="font-semibold">{selectedMentor.name}</p><p className="text-sm text-muted-foreground">{selectedMentor.current_employment || 'Program Alumni'}</p></div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Introduce Yourself</label>
              <DictationInput value={connectMessage} onChange={(val) => setConnectMessage(val)} placeholder="Tell them about yourself and why you'd like their mentorship..." className="rounded-xl" rows={3} showCleanup={false} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Areas You'd Like Help With</label>
              <Input value={areasOfInterest} onChange={(e) => setAreasOfInterest(e.target.value)} placeholder="e.g., Job search, Housing, Recovery (comma-separated)" className="rounded-xl" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={requestMentorship} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-2"><Send size={16} /> Send Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* My Profile Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>My Alumni Profile</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
              <span className="text-sm font-medium">Profile Visible to Others</span>
              <Switch checked={profileForm.profile_visible} onCheckedChange={(v) => setProfileForm({...profileForm, profile_visible: v})} />
            </div>
            <div><label className="text-sm font-medium mb-1.5 block">Bio</label><DictationInput value={profileForm.bio} onChange={(val) => setProfileForm({...profileForm, bio: val})} placeholder="Tell others about yourself..." className="rounded-xl" rows={2} showCleanup={false} /></div>
            <div><label className="text-sm font-medium mb-1.5 block">Success Story</label><DictationInput value={profileForm.success_story} onChange={(val) => setProfileForm({...profileForm, success_story: val})} placeholder="Share your journey and accomplishments..." className="rounded-xl" rows={3} showCleanup={false} /></div>
            <div><label className="text-sm font-medium mb-1.5 block">Skills (comma-separated)</label><Input value={profileForm.skills} onChange={(e) => setProfileForm({...profileForm, skills: e.target.value})} placeholder="e.g., Leadership, Cooking, Customer Service" className="rounded-xl" /></div>
            <div><label className="text-sm font-medium mb-1.5 block">Current Employment</label><Input value={profileForm.current_employment} onChange={(e) => setProfileForm({...profileForm, current_employment: e.target.value})} placeholder="e.g., Line Cook at Applebee's" className="rounded-xl" /></div>
            <div><label className="text-sm font-medium mb-1.5 block">Program Completion Date</label><Input type="date" value={profileForm.program_completed_date} onChange={(e) => setProfileForm({...profileForm, program_completed_date: e.target.value})} className="rounded-xl" /></div>
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl">
              <div><span className="text-sm font-medium">Available for Mentorship</span><p className="text-xs text-muted-foreground">Help current participants succeed</p></div>
              <Switch checked={profileForm.available_for_mentorship} onCheckedChange={(v) => setProfileForm({...profileForm, available_for_mentorship: v})} />
            </div>
            {profileForm.available_for_mentorship && (
              <>
                <div><label className="text-sm font-medium mb-1.5 block">Mentorship Areas (comma-separated)</label><Input value={profileForm.mentorship_areas} onChange={(e) => setProfileForm({...profileForm, mentorship_areas: e.target.value})} placeholder="e.g., Job Search, Housing, Recovery Support" className="rounded-xl" /></div>
                <div><label className="text-sm font-medium mb-1.5 block">Advice for Participants</label><DictationInput value={profileForm.advice_for_participants} onChange={(val) => setProfileForm({...profileForm, advice_for_participants: val})} placeholder="What advice would you give to current participants?" className="rounded-xl" rows={2} showCleanup={false} /></div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleSaveProfile} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-2"><CheckCircle size={16} /> Save Profile</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
